import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import "./SummaryPage.css";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import StatusCard from "./CardsPage.jsx";

import Charts from "./ChartSummaryPage.jsx";
import StatusCardRow from "./CardsRow.jsx";
import { useExportedData } from "../../../../contexts/ExportedDataContext.jsx";
import { useNavigation } from "../../../../contexts/NavigationContext";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useNavigate } from "react-router-dom";
import Breadcrumbs from "../../../../components/Microgrid/BreadCrumbsFile.jsx";
import { buildSimulationCrumbs } from "../../utils/simulationCrumbs";
import { ToastContainer ,toast } from "react-toastify";
import { sysConfigApi } from "../../../../services/api/sysconfigService.js";
import { useTimeseriesSysconfig } from "../../hooks/useTimeseriesSysconfig";
import { listDevicesFromSysconfig } from "../../../devices/domain/deviceModel.js";
import { useDemandChargeData } from "../../hooks/useDemandChargeData";

const SUMMARY_TAB = {
  CHARTS: 0,
  POWER_FLOW: 1,
};

export default function Summary() {
  const { timeSeriesFileName } = useNavigation();
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const [hoveredSummaryCardTitle, setHoveredSummaryCardTitle] = useState(null);
  const [summaryCardToggleRequest, setSummaryCardToggleRequest] = useState(null);
  const [summaryCardVisibility, setSummaryCardVisibility] = useState({
    Transfer: true,
    Production: true,
    Consumption: true,
    Storage: true,
  });
  const summaryCardScrollSnapshotRef = useRef(null);
  const summaryCardVisibilityRef = useRef(summaryCardVisibility);
  const [deviceByType, setDeviceByType] = useState({ EV: [], BESS: [] });
  const [prodDeviceByType, setProdDeviceByType] = useState({
    Solar: [],
    Generator: [],
  });
  const rawCustomerEmail =
    sessionStorage.getItem("selectedCustomerEmail") || "";
  const customerPrefix = rawCustomerEmail?.replace(/[@.]/g, "-") || "";

  const name = sessionStorage.getItem("microgridDesign");
  const meterName = sessionStorage.getItem("MicrogridmeterName");
  
  const token = sessionStorage.getItem("accessToken");
  const fileName = sessionStorage.getItem("timeSeriesFileName");

  const accountname = sessionStorage.getItem("storageAccountName");

  const navigate = useNavigate();

  const {
    storageAccount,
    microgridName,
    datasetName: resolvedDatasetName,
    loading: paramsLoading,
  } = useTimeseriesSysconfig();

  const { hasDemandCharge, dailyDemand } = useDemandChargeData();

  /* Write-only for now. OverlayChart calls onLoadingStateChange with a bare
   * boolean while handleLoadingStateChange below is curried on chart type, so
   * this never actually updates -- the readiness gate the page really uses is
   * shouldShowCards / hasMeaningfulData from ExportedDataContext, passed to
   * StatusCard. Kept because it is the prop contract Charts is written
   * against; fixing the signature is a render-behaviour change of its own. */
  const [, setChartLoadingState] = useState({
    production: true,
    storage: true,
    consumption: true,
    transfer: true,
  });

  // Which of the two summary views is on screen. Both panels stay mounted at
  // all times (see the render below) -- this only drives which one is visible.
  const [activeTab, setActiveTab] = useState(SUMMARY_TAB.CHARTS);
  const handleTabChange = useCallback((_event, nextTab) => {
    setActiveTab(nextTab);
  }, []);


  const {
    exportedData,
    overallExportedData,
    exportedTransferData,
    overallExportedTransferData,
    exportedStorageData,
    exportedStorageSummaryData,
    overallExportedStorageSummaryData,
    exportedProductionData,
    exportedProductionSummaryData,
    overallExportedProductionSummaryData,
    exportedDemandChargeData,
    overallExportedDemandChargeData,
    chartDataReadyStates,
    shouldShowCards,
    hasMeaningfulData,
  } = useExportedData();

  const handleLoadingStateChange = useCallback(
    (type) => (isLoading) => {
      setChartLoadingState((prev) => ({
        ...prev,
        [type]: isLoading,
      }));
    },
    [],
  );

  summaryCardVisibilityRef.current = summaryCardVisibility;
  const createScrollSnapshot = useCallback((cardTitle = null, cardElement = null) => {
    const contentElement =
      typeof document !== "undefined"
        ? document.querySelector(".content")
        : null;
    const contentRect =
      contentElement && typeof contentElement.getBoundingClientRect === "function"
        ? contentElement.getBoundingClientRect()
        : null;
    const cardRect =
      cardElement && typeof cardElement.getBoundingClientRect === "function"
        ? cardElement.getBoundingClientRect()
        : null;

    return {
      cardTitle,
      cardSurface: cardElement?.dataset.cardSurface || null,
      cardViewportTop:
        cardRect && contentRect ? cardRect.top - contentRect.top : null,
      contentElement,
      contentScrollTop:
        contentElement && Number.isFinite(contentElement.scrollTop)
          ? contentElement.scrollTop
          : null,
      contentScrollLeft:
        contentElement && Number.isFinite(contentElement.scrollLeft)
          ? contentElement.scrollLeft
          : null,
      windowScrollX:
        typeof window !== "undefined"
          ? window.scrollX || window.pageXOffset || 0
          : 0,
      windowScrollY:
        typeof window !== "undefined"
          ? window.scrollY || window.pageYOffset || 0
          : 0,
    };
  }, []);
  const preserveScrollPosition = useCallback((updateFn) => {
    const snapshot =
      summaryCardScrollSnapshotRef.current ||
      createScrollSnapshot();

    summaryCardScrollSnapshotRef.current = null;

    updateFn();

    const restoreScrollPosition = () => {
      const contentElement =
        snapshot.contentElement ||
        (typeof document !== "undefined"
          ? document.querySelector(".content")
          : null);

      const activeElement = document.activeElement;
      if (
        activeElement &&
        typeof activeElement.matches === "function" &&
        activeElement.matches("[data-card-title]")
      ) {
        activeElement.blur?.();
      }

      if (contentElement && snapshot.contentScrollTop !== null) {
        contentElement.scrollTop = snapshot.contentScrollTop;
      }
      if (contentElement && snapshot.contentScrollLeft !== null) {
        contentElement.scrollLeft = snapshot.contentScrollLeft;
      }
      if (
        typeof window !== "undefined" &&
        typeof window.scrollTo === "function"
      ) {
        window.scrollTo(snapshot.windowScrollX, snapshot.windowScrollY);
      }

      if (
        contentElement &&
        snapshot.cardTitle &&
        snapshot.cardSurface !== "sticky" &&
        snapshot.cardViewportTop !== null &&
        typeof document !== "undefined"
      ) {
        const contentRect = contentElement.getBoundingClientRect();
        const candidates = Array.from(
          document.querySelectorAll("[data-card-title]"),
        )
          .filter((element) => element.dataset.cardTitle === snapshot.cardTitle)
          .filter(
            (element) =>
              !snapshot.cardSurface ||
              element.dataset.cardSurface === snapshot.cardSurface,
          )
          .map((element) => ({
            element,
            rect: element.getBoundingClientRect(),
          }))
          .filter(({ rect }) => rect.width > 0 && rect.height > 0);

        const visibleCandidates = candidates.filter(
          ({ rect }) =>
            rect.bottom > contentRect.top && rect.top < contentRect.bottom,
        );

        const target =
          (visibleCandidates.length ? visibleCandidates : candidates)
            .sort(
              (a, b) =>
                Math.abs(
                  a.rect.top - contentRect.top - snapshot.cardViewportTop,
                ) -
                Math.abs(
                  b.rect.top - contentRect.top - snapshot.cardViewportTop,
                ),
            )
            [0]?.element || null;

        if (target) {
          const targetRect = target.getBoundingClientRect();
          const deltaTop =
            targetRect.top - contentRect.top - snapshot.cardViewportTop;

          if (Number.isFinite(deltaTop) && Math.abs(deltaTop) > 0.5) {
            contentElement.scrollTop += deltaTop;
          }
        }
      }
    };

    if (
      typeof window !== "undefined" &&
      typeof window.requestAnimationFrame === "function"
    ) {
      window.requestAnimationFrame(() => {
        restoreScrollPosition();
        window.requestAnimationFrame(restoreScrollPosition);
      });
      window.setTimeout(restoreScrollPosition, 0);
      window.setTimeout(restoreScrollPosition, 100);
      window.setTimeout(restoreScrollPosition, 250);
      window.setTimeout(restoreScrollPosition, 500);
    } else {
      restoreScrollPosition();
    }
  }, [createScrollSnapshot]);
  const captureSummaryCardScrollSnapshot = useCallback((cardTitle, cardElement) => {
    summaryCardScrollSnapshotRef.current = createScrollSnapshot(
      cardTitle,
      cardElement,
    );
  }, [createScrollSnapshot]);
  const handleSummaryCardToggle = useCallback((title) => {
    const nextVisible = summaryCardVisibilityRef.current?.[title] === false;

    preserveScrollPosition(() => {
      setSummaryCardToggleRequest((previousRequest) => ({
        title,
        visible: nextVisible,
        nonce: (previousRequest?.nonce || 0) + 1,
      }));
      setSummaryCardVisibility((previousVisibility) => ({
        ...previousVisibility,
        [title]: nextVisible,
      }));
    });
  }, [preserveScrollPosition]);
  const handleSummaryCardVisibilityChange = useCallback((nextVisibility) => {
    preserveScrollPosition(() => {
      setSummaryCardVisibility((previousVisibility) => {
        const nextState = {
          ...previousVisibility,
          ...nextVisibility,
        };

        const isUnchanged = Object.keys(nextState).every(
          (key) => previousVisibility[key] === nextState[key],
        );

        return isUnchanged ? previousVisibility : nextState;
      });
    });
  }, [preserveScrollPosition]);
  useEffect(() => {
    const fetchDevices = async () => {
      const customer = sessionStorage.getItem("selectedCustomerEmail") || sessionStorage.getItem("email");

      if (!customer || !token) return;

      try {
        const data = await sysConfigApi({
          accountname,
          simulationName: microgridName,
          datasetName: resolvedDatasetName,
          token,
        });

        const mapping = { EV: [], BESS: [] };
        const mapping1 = { Solar: [], Generator: [] };

        const allDevices = listDevicesFromSysconfig(data);

        allDevices
          .filter((d) => d.type === "ev")
          .forEach((d) => mapping.EV.push(d.raw));

        allDevices
          .filter((d) => d.type === "battery")
          .forEach((d) => mapping.BESS.push(d.raw));

        allDevices
          .filter((d) => d.type === "solar")
          .forEach((d) => mapping1.Solar.push(d.raw));

        allDevices
          .filter((d) => d.type === "generator")
          .forEach((d) => mapping1.Generator.push(d.raw));

        setDeviceByType(mapping);
        setProdDeviceByType(mapping1);
      } catch (error) {
        setDeviceByType({});
        setDeviceByType({ Solar: [], Generator: [] });
        console.error("Error fetching devices:", error);
      }
    };
    if (token && accountname && resolvedDatasetName && !paramsLoading) {
      fetchDevices();
    }
  }, [token, accountname, resolvedDatasetName, paramsLoading]);


const bessMaxCapacity = useMemo(() => {
    return {
      BESS: deviceByType?.BESS?.map((dev) => dev.maximum_capacity_kwh) || [],
    };
  }, [deviceByType?.BESS]);

  const evMaxCapacity = useMemo(() => {
    return {
      EV: deviceByType?.EV?.map((dev) => dev.maximum_capacity_kwh) || [],
    };
  }, [deviceByType?.EV]);

  const deviceNames = useMemo(() => {
    return {
      BESS: deviceByType?.BESS?.map((dev) => dev.name) || [],
      EV: deviceByType?.EV?.map((dev) => dev.name) || [],
    };
  }, [deviceByType?.BESS, deviceByType?.EV]);

  const proddeviceNames = useMemo(() => {
      return {
        Solar: prodDeviceByType?.Solar?.map((dev) => dev.name) || [],
        Generator: prodDeviceByType?.Generator?.map((dev) => dev.name) || [],
      };
    }, [prodDeviceByType?.Solar, prodDeviceByType?.Generator]);
  const summaryHeader = (
    <StatusCardRow
      exportedData={exportedData}
      exportedTransferData={exportedTransferData}
      exportedStorageSummaryData={exportedStorageSummaryData}
      exportedProductionSummaryData={exportedProductionSummaryData}
      exportedDemandChargeData={exportedDemandChargeData}
      siteProductionSummaryData={overallExportedProductionSummaryData}
      siteStorageSummaryData={overallExportedStorageSummaryData}
      onCardHoverChange={setHoveredSummaryCardTitle}
      onCardToggle={handleSummaryCardToggle}
      onCardPointerDown={captureSummaryCardScrollSnapshot}
      summaryCardVisibility={summaryCardVisibility}
      chartDataReadyStates={chartDataReadyStates}
      shouldShowCards={shouldShowCards}
      hasMeaningfulData={hasMeaningfulData}
    />
  );

  const simulationDisplayName =
    sessionStorage.getItem("simulationDisplayName") || "";

  return (
    <div className="container summary-page">
      <h2 className="page-heading">
        Summary{simulationDisplayName ? ` \u2014 ${simulationDisplayName}` : ""}
      </h2>


<Breadcrumbs
  crumbs={buildSimulationCrumbs({
    jobTitle: sessionStorage.getItem("jobTitle"),
    customerFirstName: sessionStorage.getItem("customerFirstName"),
    customerPrefix,
    microgridDesign: name,
    fileName,
  })}
/>

      <Tabs
        className="summary-tabs"
        value={activeTab}
        onChange={handleTabChange}
        aria-label="Summary views"
      >
        <Tab
          className="summary-tabs__tab"
          label="Charts"
          id="summary-tab-charts"
          aria-controls="summary-tabpanel-charts"
        />
        <Tab
          className="summary-tabs__tab"
          label="Power Flow"
          id="summary-tab-power-flow"
          aria-controls="summary-tabpanel-power-flow"
        />
      </Tabs>

      {/*
        Both tab panels stay mounted for the whole life of the page and are
        toggled with the `hidden` attribute (display:none) rather than being
        conditionally rendered.

        This is load-bearing for the Charts panel: OverlayChart rebuilds
        `chartData` from context on mount and fires several
        productionWorkerService / storageWorkerService jobs off it, and neither
        worker service caches results. Unmounting it on a tab switch would
        replay that whole pipeline on the way back, and would also throw away
        the user's chart state (period/date selection, zoom range, per-series
        visibility) and the ECharts instance itself. Keeping the node in the
        DOM avoids all of that; the chart's own ResizeObserver re-measures it
        when the panel becomes visible again.
      */}
      <div
        className="summary-tab-panel summary-tab-panel--charts"
        role="tabpanel"
        id="summary-tabpanel-charts"
        aria-labelledby="summary-tab-charts"
        hidden={activeTab !== SUMMARY_TAB.CHARTS}
      >
        <div className="charts">
          <Charts
            csvData={timeSeriesFileName}
            onLoadingStateChange={handleLoadingStateChange}
            bessMaxCapacity={bessMaxCapacity}
            evMaxCapacity={evMaxCapacity}
            deviceNames={deviceNames}
            proddeviceNames={proddeviceNames}
            hoveredSummaryCardTitle={hoveredSummaryCardTitle}
            summaryCardVisibility={summaryCardVisibility}
            summaryCardToggleRequest={summaryCardToggleRequest}
            onSummaryCardVisibilityChange={handleSummaryCardVisibilityChange}
            summaryHeader={summaryHeader}
            dailyDemand={dailyDemand}
            hasDemandCharge={hasDemandCharge}
          />
        </div>
      </div>

      <div
        className="summary-tab-panel summary-tab-panel--power-flow"
        role="tabpanel"
        id="summary-tabpanel-power-flow"
        aria-labelledby="summary-tab-power-flow"
        hidden={activeTab !== SUMMARY_TAB.POWER_FLOW}
      >
        <div className="cards-shell">
          <div className="cards">
            <StatusCard
              exportedData={overallExportedData}
              exportedTransferData={overallExportedTransferData}
              exportedStorageSummaryData={overallExportedStorageSummaryData}
              exportedProductionSummaryData={
                overallExportedProductionSummaryData
              }
              exportedDemandChargeData={overallExportedDemandChargeData}
              chartDataReadyStates={chartDataReadyStates}
              shouldShowCards={shouldShowCards}
              hasMeaningfulData={hasMeaningfulData}
              interactive={false}
            />
          </div>
        </div>
      </div>

    </div>
  );
}
